/**
 * 텍스트 유틸리티 함수
 * 공백, 줄바꿈, 마크다운 기호 차이를 허용하면서 텍스트를 찾고 교체하는 함수
 */

/**
 * 공백/줄바꿈/마크다운 기호 차이를 허용하면서 텍스트를 찾고 교체하는 함수
 * 
 * @param {string} fullText - 전체 텍스트 (artifact.content)
 * @param {string} searchString - 찾을 텍스트 (사용자가 드래그한 selected_text)
 * @param {string} replacementString - 교체할 텍스트 (AI가 수정한 modified)
 * @returns {{ok: boolean, content: string}} - 교체 성공 여부와 결과 텍스트
 */
function buildVisibleFingerprintWithMap(text) {
  let out = '';
  const map = [];
  let lastWasSpace = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    // 마크다운/구조 문자를 fingerprint에서 제거 (렌더링 텍스트에 가깝게)
    if (/[*_`#>\[\]\(\)!|]/.test(ch)) continue;

    if (/\s/.test(ch)) {
      if (lastWasSpace) continue;
      out += ' ';
      map.push(i);
      lastWasSpace = true;
      continue;
    }

    out += ch.toLowerCase();
    map.push(i);
    lastWasSpace = false;
  }

  // trim 처리(앞/뒤 공백 제거) + map 동기화
  const trimmedLeft = out.replace(/^\s+/, '');
  const leftTrimCount = out.length - trimmedLeft.length;
  const trimmed = trimmedLeft.replace(/\s+$/, '');
  const rightTrimCount = trimmedLeft.length - trimmed.length;

  const trimmedMap = map.slice(leftTrimCount, map.length - rightTrimCount);
  return { text: trimmed, map: trimmedMap };
}

function buildVisibleFingerprint(text) {
  return text
    .replace(/[*_`#>\[\]\(\)!|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * fullText(마크다운 원문)에서 searchString(렌더링에서 드래그한 '보이는 텍스트')의 위치를
 * (start,end) 원문 인덱스로 찾아준다. (최대한 가볍고 결정적으로)
 */
export function findReplacementSpan(fullText, searchString) {
  if (!fullText || !searchString) return { ok: false };
  const searchTrimmed = searchString.trim();
  if (!searchTrimmed) return { ok: false };

  const fullFp = buildVisibleFingerprintWithMap(fullText);
  const searchFp = buildVisibleFingerprint(searchTrimmed);
  if (!searchFp || !fullFp.text) return { ok: false };

  const fpIndex = fullFp.text.indexOf(searchFp);
  if (fpIndex === -1) return { ok: false };

  const fpEnd = fpIndex + searchFp.length - 1;
  const startOrig = fullFp.map[fpIndex];
  const endOrig = (fullFp.map[fpEnd] ?? fullText.length - 1) + 1;

  if (typeof startOrig !== 'number' || typeof endOrig !== 'number' || endOrig <= startOrig) {
    return { ok: false };
  }

  return { ok: true, start: startOrig, end: endOrig };
}

export function smartReplace(fullText, searchString, replacementString) {
  if (!fullText || !searchString) {
    console.warn('[smartReplace] fullText 또는 searchString이 비어있음');
    return { ok: false, content: fullText };
  }

  const searchTrimmed = searchString.trim();
  if (!searchTrimmed) {
    console.warn('[smartReplace] searchString이 공백만 있음');
    return { ok: false, content: fullText };
  }

  // Step 1: Exact Match (정확 일치)
  const exactIndex = fullText.indexOf(searchTrimmed);
  if (exactIndex !== -1) {
    return {
      ok: true,
      content: fullText.substring(0, exactIndex) + 
               replacementString + 
               fullText.substring(exactIndex + searchTrimmed.length)
    };
  }

  // Step 2: Whitespace Normalization (공백 정규화)
  // searchString의 모든 연속된 공백/줄바꿈을 \s+로 변환하여 유연한 정규식 패턴 생성
  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  // 공백 문자를 \s+로 변환 (여러 공백/줄바꿈을 하나의 \s+로 처리)
  const words = searchTrimmed.split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) {
    return { ok: false, content: fullText };
  }

  // 각 단어를 이스케이프하고 단어 사이에 \s+를 넣어 정규식 패턴 생성
  const pattern = words.map(w => escapeRegex(w)).join('\\s+');
  const regex = new RegExp(pattern, 'i'); // 대소문자 무시
  
  const match = regex.exec(fullText);
  if (match) {
    const matchedText = match[0];
    const matchIndex = match.index;
    
    return {
      ok: true,
      content: fullText.substring(0, matchIndex) + 
               replacementString + 
               fullText.substring(matchIndex + matchedText.length)
    };
  }

  // Step 2.5: 마크다운 기호를 무시한 정규식 매칭
  // 각 단어 사이에 마크다운 기호(*, #, `, [, ! 등)가 올 수 있도록 패턴 생성
  const markdownPattern = words
    .map(w => escapeRegex(w))
    .join('(?:\\s*[*#`\\[\\]!_-]*\\s*)+'); // 단어 사이에 마크다운 기호 허용
  const markdownRegex = new RegExp(markdownPattern, 'i');
  
  const markdownMatch = markdownRegex.exec(fullText);
  if (markdownMatch) {
    const matchedText = markdownMatch[0];
    const matchIndex = markdownMatch.index;
    
    return {
      ok: true,
      content: fullText.substring(0, matchIndex) + 
               replacementString + 
               fullText.substring(matchIndex + matchedText.length)
    };
  }

  // Step 3: Fingerprint 매칭 (선형시간, 큰 문서에서도 덜 뻑임)
  const span = findReplacementSpan(fullText, searchTrimmed);
  if (span.ok) {
    return {
      ok: true,
      content: fullText.substring(0, span.start) + replacementString + fullText.substring(span.end),
    };
  }

  console.warn('[smartReplace] 모든 매칭 시도 실패', {
    searchLength: searchTrimmed.length,
    fullTextLength: fullText.length,
    searchPreview: searchTrimmed.substring(0, 100),
    fullTextPreview: fullText.substring(0, 200)
  });

  return { ok: false, content: fullText };
}
